/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import {
  STATUS_CONFIG,
  type ReservationRequest,
  type ReservationRequestStatus,
  type LinkedReservation,
} from "./types";
import { formatDate, formatTime } from "./utils";
import { useAuth } from "@app/vue/providers/Auth";

export interface ReservationRequestCardProps {
  request: ReservationRequest;
  linkedReservation?: LinkedReservation | null;
  onClick: (request: ReservationRequest) => void;
  onAccept?: (request: ReservationRequest) => void;
  onReject?: (request: ReservationRequest) => void;
  hideActions?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

export const ReservationRequestCard =
  defineComponent<ReservationRequestCardProps>({
    name: "ReservationRequestCard",
    props: [
      "request",
      "linkedReservation",
      "onClick",
      "onAccept",
      "onReject",
      "hideActions",
      "selected",
      "onToggleSelect",
    ],
    setup(props) {
      const { user } = useAuth();
      const { t } = useLocalization();
      const timezone = user.value?.restaurant?.timezone || "UTC";
      return () => {
        const request = props.request;
        const statusConfig = STATUS_CONFIG[
          request.status as ReservationRequestStatus
        ] || {
          labelKey: request.status,
          bgClass: "bg-gray-500/10",
          textClass: "",
          icon: "help",
        };
        const isPending = request.status === "pending";
        const requestedTags = (request as any).requested_tags || [];

        // Status display
        let displayLabel = t(statusConfig.labelKey);
        let displayBg = statusConfig.bgClass;
        let displayText = statusConfig.textClass;
        let displayIcon = statusConfig.icon;

        if (isPending) {
          displayBg = "bg-error/15 border border-error/60";
          displayText = "text-error";
          displayLabel = "Pending";
          displayIcon = "schedule";
        }

        return (
          <div
            class={`bg-base-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer group border border-base-300/30 hover:border-base-content/20`}
            onClick={() => props.onClick(request)}
          >
            <div class="px-3 py-3 sm:px-4 sm:py-3.5">
              {/* Main row */}
              <div class="flex min-w-0 items-center gap-3 sm:gap-4 md:gap-6">
                {/* Checkbox for batch selection — hidden on mobile */}
                {props.onToggleSelect && (
                  <div
                    class="flex-shrink-0 hidden sm:flex items-center"
                    onClick={(e: Event) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-accent"
                      checked={props.selected}
                      onChange={() => props.onToggleSelect!(request.id)}
                    />
                  </div>
                )}

                {/* 1. Time */}
                <div class="flex-shrink-0 self-start" style="width: 80px;">
                  <span class="text-base sm:text-xl font-bold text-accent leading-tight whitespace-nowrap">
                    {formatTime(request.requested_time, timezone)}
                  </span>
                  <div class="text-[10px] text-base-content/50 mt-0.5 font-medium uppercase tracking-wide">
                    {formatDate(request.requested_time, timezone, {
                      today: t("general.date.today"),
                      tomorrow: t("general.date.tomorrow"),
                    })}
                  </div>
                </div>

                {/* 2. Status badge — hidden on mobile */}
                <div class="flex-shrink-0 hidden sm:block" style="width: 95px;">
                  <div
                    class={`relative flex items-center w-full px-2 rounded-md text-[10px] font-semibold leading-tight ${displayBg} ${displayText}`}
                    style={`height: 32px;${isPending ? " animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;" : ""}`}
                  >
                    <span class="material-symbols-outlined text-[12px] absolute left-2 top-1/2 -translate-y-1/2">
                      {displayIcon}
                    </span>
                    <span class="leading-tight w-full text-center pl-3">
                      {displayLabel}
                    </span>
                  </div>
                </div>

                {/* 3. Guest Name + party details under */}
                <div class="min-w-0 flex-1 sm:flex-none sm:w-[180px]">
                  <h3 class="font-semibold text-accent text-sm sm:text-base truncate">
                    {request.party_name ||
                      request.customer_name ||
                      t("reservations.general.guest_label")}
                  </h3>
                  {/* Party details under name on mobile + tablet */}
                  <div class="flex md:hidden items-center gap-2 mt-0.5">
                    <span class="flex items-center gap-0.5 text-xs text-base-content/70">
                      <span class="material-symbols-outlined text-xs">
                        group
                      </span>
                      {request.party_size}
                    </span>
                    {request.duration_mins && (
                      <span class="flex items-center gap-0.5 text-xs text-base-content/55">
                        <span class="material-symbols-outlined text-xs">
                          schedule
                        </span>
                        {request.duration_mins}m
                      </span>
                    )}
                  </div>
                </div>

                {/* 4. Party size + duration — desktop only */}
                <div class="hidden md:flex items-center gap-3 flex-shrink-0">
                  <div class="flex items-center gap-1 text-sm text-base-content/70">
                    <span class="material-symbols-outlined text-base">
                      group
                    </span>
                    <span class="font-medium">{request.party_size}</span>
                  </div>
                  {request.duration_mins && (
                    <div class="flex items-center gap-1 text-xs text-base-content/55">
                      <span class="material-symbols-outlined text-sm">
                        schedule
                      </span>
                      <span>{request.duration_mins} min</span>
                    </div>
                  )}
                </div>

                {/* 5. Tags + Notes — tablet and desktop */}
                <div class="hidden md:flex min-w-0 items-center gap-1.5 flex-1 justify-end overflow-hidden">
                  {requestedTags.map((tag: any) => (
                    <span
                      key={tag.id}
                      class="inline-flex min-w-0 max-w-[120px] shrink items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                      style={{
                        backgroundColor: tag.color
                          ? `${tag.color}15`
                          : "oklch(var(--b3))",
                        color: tag.color || undefined,
                      }}
                      title={tag.name}
                    >
                      {tag.icon && (
                        <span class="material-symbols-outlined text-sm flex-shrink-0">
                          {tag.icon}
                        </span>
                      )}
                      <span class="truncate min-w-0">{tag.name}</span>
                    </span>
                  ))}
                  {request.notes && (
                    <div
                      class="inline-flex min-w-0 max-w-[180px] shrink items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20"
                      title={request.notes}
                    >
                      <span class="material-symbols-outlined text-sm flex-shrink-0">
                        sticky_note_2
                      </span>
                      <span class="truncate min-w-0">{request.notes}</span>
                    </div>
                  )}
                </div>

                {/* Accept/Decline — desktop only */}
                {isPending && !props.hideActions && props.onAccept && (
                  <div
                    class="flex-shrink-0 hidden sm:flex items-center gap-1.5 ml-auto"
                    onClick={(e: Event) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-success text-success-content hover:brightness-110 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onAccept!(request);
                      }}
                    >
                      <span class="material-symbols-outlined text-sm">
                        check
                      </span>
                      {t("reservations.general.accept")}
                    </button>
                    {props.onReject && (
                      <button
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-base-content/50 hover:text-error hover:bg-error/10 border border-base-content/10 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onReject!(request);
                        }}
                      >
                        <span class="material-symbols-outlined text-sm">
                          close
                        </span>
                        {t("reservations.general.decline")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile: tags + notes details row */}
            {(request.notes || requestedTags.length > 0) && (
              <div class="flex md:hidden items-center gap-2 p-2 border-t border-base-300/30 flex-wrap">
                {request.notes && (
                  <div class="flex items-center gap-1 text-xs text-base-content/60 flex-1 min-w-0">
                    <span class="material-symbols-outlined text-sm flex-shrink-0">
                      sticky_note_2
                    </span>
                    <span class="truncate">{request.notes}</span>
                  </div>
                )}
                {requestedTags.map((tag: any) => (
                  <span
                    key={tag.id}
                    class="inline-flex min-w-0 max-w-full items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-current/15"
                    style={{
                      backgroundColor: tag.color
                        ? `${tag.color}20`
                        : "oklch(var(--b3))",
                      color: tag.color || undefined,
                    }}
                    title={tag.name}
                  >
                    {tag.icon && (
                      <span class="material-symbols-outlined text-xs flex-shrink-0">
                        {tag.icon}
                      </span>
                    )}
                    <span class="truncate min-w-0">{tag.name}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      };
    },
  });
